import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, Eye, EyeOff, Loader2, Shield } from 'lucide-react';
import LightLogo from '@/components/LightLogo';
import LightBackground from '@/components/LightBackground';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ApplicationData {
  name: string;
  email: string;
  plan: 'monthly' | 'lifetime';
  amount_paid?: number;
  stripe_customer_id?: string;
}

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [applicationData, setApplicationData] = useState<ApplicationData | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [errors, setErrors] = useState<{ name?: string; password?: string; confirmPassword?: string }>({});

  // Fire Meta Pixel Purchase event when payment data is available
  useEffect(() => {
    if (applicationData && applicationData.amount_paid) {
      // Fire Meta Pixel Purchase event
      if (typeof window !== 'undefined' && (window as any).fbq) {
        (window as any).fbq('track', 'Purchase', {
          value: applicationData.amount_paid,
          currency: 'USD',
          content_type: 'product',
          content_name: applicationData.plan === 'lifetime' ? 'Lifetime Access' : 'Monthly Subscription',
        });
        console.log('[META PIXEL] Purchase event fired:', {
          value: applicationData.amount_paid,
          plan: applicationData.plan,
        });
      }
    }
  }, [applicationData]);

  useEffect(() => {
    document.body.style.cursor = 'none';
    
    const initializeData = async () => {
      // Check for test mode (for Meta Pixel testing)
      const isTestMode = searchParams.get('test') === 'true';
      if (isTestMode) {
        const testData = {
          name: 'Test User',
          email: 'test@facelessaura.club',
          plan: (searchParams.get('plan') as 'monthly' | 'lifetime') || 'lifetime',
          amount_paid: searchParams.get('plan') === 'monthly' ? 4.99 : 37,
          stripe_customer_id: 'cus_test_pixel',
        };
        setApplicationData(testData);
        setName(testData.name);
        setIsVerifying(false);
        console.log('[META PIXEL TEST] Payment success page loaded with test data');
        return;
      }

      // First try sessionStorage (for users coming from /apply flow)
      const storedData = sessionStorage.getItem('applicationData');
      if (storedData) {
        const parsed = JSON.parse(storedData);
        setApplicationData(parsed);
        setName(parsed.name || '');
        setIsVerifying(false);
        return;
      }

      // If no sessionStorage, check for Stripe session_id in URL
      const sessionId = searchParams.get('session_id');
      if (sessionId) {
        try {
          const { data, error } = await supabase.functions.invoke('verify-checkout-session', {
            body: { session_id: sessionId },
          });

          if (error) throw error;
          if (!data.success) throw new Error(data.error || 'Payment verification failed');

          // Set application data from Stripe session including amount paid
          const stripeData = {
            name: data.name || 'Guest',
            email: data.email,
            plan: data.plan || 'monthly',
            amount_paid: data.amount_paid,
            stripe_customer_id: data.stripe_customer_id,
          };
          setApplicationData(stripeData);
          // Pre-fill name, but leave empty if it's "Guest" so user enters their real name
          setName(stripeData.name === 'Guest' ? '' : stripeData.name);
          setIsVerifying(false);
          return;
        } catch (err) {
          console.error('Error verifying Stripe session:', err);
          toast.error('Could not verify payment. Please contact support.');
          navigate('/apply');
          return;
        }
      }

      // No data found at all
      toast.error('Session expired. Please apply again.');
      navigate('/apply');
    };

    initializeData();

    return () => {
      document.body.style.cursor = 'auto';
    };
  }, [navigate, searchParams]);

  const validateForm = () => {
    const newErrors: { name?: string; password?: string; confirmPassword?: string } = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    } else if (name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !applicationData) return;

    setIsLoading(true);

    try {
      // First, try to sign up the user
      const trimmedName = name.trim();
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: applicationData.email,
        password: password,
        options: {
          emailRedirectTo: window.location.origin,
          data: {
            name: trimmedName,
          },
        },
      });

      if (signUpError) {
        const msg = (signUpError as any)?.message?.toLowerCase?.() ?? '';
        const isAlreadyRegistered =
          msg.includes('already registered') ||
          msg.includes('already been registered') ||
          msg.includes('user already registered');

        if (isAlreadyRegistered) {
          // User already exists - update their password via edge function
          const { error: updateError } = await supabase.functions.invoke('update-user-password', {
            body: { email: applicationData.email, password: password },
          });

          if (updateError) {
            console.error('Error updating password:', updateError);
            throw new Error('Failed to update password. Please try again.');
          }

          // Now sign in with the new password
          const { error: signInError } = await supabase.auth.signInWithPassword({
            email: applicationData.email,
            password: password,
          });
          if (signInError) throw signInError;
        } else {
          throw signUpError;
        }
      } else {
        if (!signUpData.user) {
          throw new Error('Failed to create account');
        }
      }

      // Activate membership + send welcome email automatically
      // Include amount_paid from Stripe for accurate CRM tracking
      // Use the name from the form (user may have edited it)
      const { data: clientData, error: membershipError } = await supabase.functions.invoke('create-client-record', {
        body: {
          name: trimmedName,
          email: applicationData.email,
          plan: applicationData.plan,
          amount_paid: applicationData.amount_paid,
          stripe_customer_id: applicationData.stripe_customer_id,
        },
      });

      if (membershipError) {
        console.error('Error activating membership:', membershipError);
        throw membershipError;
      }

      console.log('Client record created:', clientData?.isNewClient ? 'new client, welcome email sent' : 'existing client updated');

      // Clear session storage
      sessionStorage.removeItem('applicationData');

      toast.success('Account created successfully!');

      // Redirect to dashboard immediately
      navigate('/dashboard');

    } catch (error: any) {
      console.error('Error creating account:', error);
      toast.error(error.message || 'Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying || !applicationData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col items-center justify-center gap-4">
        <LightBackground variant="subtle" />
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        <p className="text-slate-600 text-sm">Verifying payment...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      <LightBackground variant="default" />

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          {/* Success Header */}
          <div className="text-center mb-8 flex flex-col items-center">
            <LightLogo size="lg" />
            
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3, type: "spring" }}
              className="mt-6 mb-4 w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-green-100 to-green-50 border border-green-300 flex items-center justify-center shadow-lg"
            >
              <Check className="w-8 h-8 text-green-600" />
            </motion.div>

            <h1 className="text-2xl md:text-3xl font-bold mb-2">
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Payment Successful</span>
            </h1>
            <p className="text-slate-500 text-sm">
              Complete your account setup below
            </p>
          </div>

          {/* Plan confirmation */}
          <div className="mb-8 p-4 bg-white border border-blue-100 rounded-xl shadow-md text-center">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Your Plan</p>
            <p className="text-blue-600 font-semibold text-lg">
              {applicationData.plan === 'lifetime' ? 'Lifetime Access' : 'Monthly Subscription'}
            </p>
            {applicationData.amount_paid && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Amount Paid</p>
                <p className="text-slate-900 font-bold text-xl">
                  ${applicationData.amount_paid.toFixed(2)}
                </p>
              </div>
            )}
          </div>

          {/* Account Setup Form */}
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6">
            <h2 className="text-blue-600 text-sm font-semibold tracking-wide uppercase mb-6 text-center">
              Complete Your Account
            </h2>

            <form onSubmit={handleCreateAccount} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-700 text-sm font-medium">
                  Your Name
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="bg-slate-50 border-slate-200 focus:border-blue-400 focus:ring-blue-400 text-slate-900"
                  placeholder="Enter your full name"
                />
                {errors.name && <p className="text-red-500 text-xs">{errors.name}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-500 text-sm font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={applicationData.email}
                  disabled
                  className="bg-slate-100 border-slate-200 text-slate-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700 text-sm font-medium">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-slate-50 border-slate-200 focus:border-blue-400 focus:ring-blue-400 text-slate-900 pr-10"
                    placeholder="Min. 8 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-xs">{errors.password}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-slate-700 text-sm font-medium">
                  Confirm Password
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="bg-slate-50 border-slate-200 focus:border-blue-400 focus:ring-blue-400 text-slate-900 pr-10"
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="text-red-500 text-xs">{errors.confirmPassword}</p>}
              </div>

              <Button
                type="submit"
                size="lg"
                disabled={isLoading}
                className="w-full mt-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Creating Account...
                  </>
                ) : (
                  'Create Account & Enter'
                )}
              </Button>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
